/**
 * Module Réception — Sprint 1.
 *
 * Workflow :
 * 1. Liste des réceptions (par défaut)
 * 2. "Nouvelle réception" → formulaire BL (fournisseur, n° BL)
 * 3. Mode photo continue → upload multi
 * 4. Bouton "Analyser avec IA" → Claude Sonnet 4.5 Vision groupe
 * 5. Tableau consolidé éditable (qty, désignation, suppression)
 *
 * Dépendances : axios, sonner toast, lucide-react, shadcn buttons.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Camera, Loader2, Trash2, Sparkles, ArrowLeft, Package,
  Image as ImageIcon, AlertTriangle, CheckCircle2, X, Truck, FileText, ScanLine,
  RotateCcw, Trash, Undo2, Lock, Unlock, Layers, Search,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import ProductAutocomplete, { preloadCatalog } from './ProductAutocomplete';
import BLCameraDialog from './BLCameraDialog';
import ReceptionLotsDialog from './ReceptionLotsDialog';
import QRScanDialog from './QRScanDialog';
import { preprocessBL } from '../../../utils/imagePreprocess';
import { useAuth } from '../../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });
const FILE_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper: l'utilisateur est-il privilégié (admin ou manager) ?
const usePrivileged = () => {
  const { user, isAdmin } = useAuth();
  return isAdmin || user?.role === 'manager';
};

// ─────────────────────────────────────────────────────────────
// Liste des réceptions
// ─────────────────────────────────────────────────────────────
const ReceptionListView = ({ onOpen, onCreate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [view, setView] = useState('active'); // active | trash
  // Filtre par statut. Si l'URL a `?goto=reception-pending`, démarre sur les
  // réceptions soumises (en attente de validation) — sinon "all".
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all';
    const p = new URLSearchParams(window.location.search);
    return p.get('goto') === 'reception-pending' ? 'submitted' : 'all';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [qrScanOpen, setQrScanOpen] = useState(false);
  const isPrivileged = usePrivileged();

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const url = view === 'trash' ? `${API}/receptions/trash` : `${API}/receptions`;
      const r = await axios.get(url, { headers: auth() });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'erreur inconnue';
      setLoadError(msg);
      setItems([]);
    } finally { setLoading(false); }
  }, [view]);
  useEffect(() => { reload(); }, [reload]);

  // Préchauffe le catalogue produits en arrière-plan (utilisé par l'autocomplétion)
  useEffect(() => {
    preloadCatalog();
  }, []);

  const STATUS_LABELS = {
    draft: { label: 'Brouillon', cls: 'bg-zinc-100 text-zinc-700' },
    analyzed: { label: 'Analysée IA', cls: 'bg-blue-100 text-blue-700' },
    submitted: { label: 'Soumise', cls: 'bg-amber-100 text-amber-700' },
    validated: { label: 'Validée', cls: 'bg-emerald-100 text-emerald-700' },
    rejected: { label: 'Rejetée', cls: 'bg-red-100 text-red-700' },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-zinc-900">
            {view === 'trash' ? 'Corbeille des réceptions' : 'Réceptions de produits'}
          </h2>
          <p className="text-sm text-zinc-500">
            {view === 'trash'
              ? 'Réceptions supprimées — restaurables ou à vider définitivement.'
              : 'Scannez le Bon de Livraison ou photographiez chaque produit — l\'IA fait le reste.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isPrivileged && (
            <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-sm">
              <button
                onClick={() => setView('active')}
                className={`px-3 py-1.5 transition ${view === 'active' ? 'bg-[#5B3B8C] text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
                data-testid="reception-view-active"
              >Actives</button>
              <button
                onClick={() => setView('trash')}
                className={`px-3 py-1.5 transition border-l border-zinc-200 ${view === 'trash' ? 'bg-red-600 text-white' : 'bg-white text-zinc-700 hover:bg-red-50'}`}
                data-testid="reception-view-trash"
              ><Trash className="w-3.5 h-3.5 inline mr-1" />Corbeille</button>
            </div>
          )}
          {view === 'active' && (
            <Button
              onClick={onCreate}
              className="bg-[#5B3B8C] hover:bg-[#4A2F73] text-white"
              data-testid="reception-new-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Nouvelle réception
            </Button>
          )}
        </div>
      </div>

      {view === 'active' && (
        <ReceptionStatusFilter items={items} statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
      )}

      {view === 'active' && (
        <div className="flex items-center gap-2" data-testid="reception-search-row">
          <div className="relative flex-1" data-testid="reception-search-wrapper">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <input
              type="search"
              inputMode="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par N° RCP ou N° BL…"
              className="w-full h-11 pl-9 pr-9 text-base sm:text-sm rounded-lg border border-zinc-300 bg-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0F766E] focus:border-[#0F766E]"
              data-testid="reception-search-input"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600"
                data-testid="reception-search-clear"
                aria-label="Effacer la recherche"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            type="button"
            onClick={() => setQrScanOpen(true)}
            className="h-11 px-3 bg-[#0F766E] hover:bg-[#0a5851] text-white shrink-0"
            data-testid="reception-scan-qr-btn"
            title="Scanner un QR d'étiquette"
          >
            <ScanLine className="w-5 h-5" />
            <span className="hidden sm:inline ml-1.5 text-sm">Scanner QR</span>
          </Button>
        </div>
      )}

      <QRScanDialog
        open={qrScanOpen}
        onClose={() => setQrScanOpen(false)}
        onDetected={(rcp) => {
          setSearchQuery(rcp);
          toast.success(`QR détecté : ${rcp}`);
        }}
      />

      {loading ? (
        <div className="text-center py-12 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : loadError ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg border-red-200 bg-red-50/30 text-red-700">
          <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-red-400" />
          <p className="font-medium">Impossible de charger les réceptions</p>
          <p className="text-xs text-red-600 mt-1">{loadError}</p>
          <Button onClick={reload} variant="outline" size="sm" className="mt-3 border-red-300 text-red-700 hover:bg-red-50" data-testid="reception-retry-btn">
            Réessayer
          </Button>
        </div>
      ) : !items.length ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg border-zinc-200 text-zinc-500">
          <Truck className="w-12 h-12 mx-auto mb-2 text-zinc-300" />
          <p>Aucune réception encore. Cliquez sur « Nouvelle réception » pour démarrer.</p>
        </div>
      ) : (
        (() => {
          const q = (searchQuery || '').trim().toLowerCase();
          const baseStatus = view === 'trash'
            ? items
            : (statusFilter === 'all'
                ? items
                : statusFilter === 'draft'
                  ? items.filter((r) => r.status === 'draft' || r.status === 'analyzed')
                  : items.filter((r) => r.status === statusFilter));
          const visible = q
            ? baseStatus.filter((r) => {
                const haystack = [
                  r.validation_number,
                  r.bl_number,
                  r.fournisseur,
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(q);
              })
            : baseStatus;
          if (!visible.length) {
            return (
              <div className="text-center py-10 border-2 border-dashed rounded-lg border-zinc-200 text-zinc-500" data-testid="reception-empty-filter">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
                <p className="text-sm">
                  {q
                    ? <>Aucune réception ne correspond à <span className="font-mono font-semibold text-zinc-700">« {searchQuery} »</span>.</>
                    : 'Aucune réception dans ce filtre.'}
                </p>
                {q
                  ? <button onClick={() => setSearchQuery('')} className="text-xs text-[#0F766E] underline mt-2" data-testid="reception-clear-search-link">Effacer la recherche</button>
                  : <button onClick={() => setStatusFilter('all')} className="text-xs text-[#5B3B8C] underline mt-2">Voir toutes</button>}
              </div>
            );
          }
          return (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-xs uppercase text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Fournisseur</th>
                <th className="px-3 py-2 text-left">N° BL</th>
                <th className="px-3 py-2 text-left">N° Validation</th>
                <th className="px-3 py-2 text-center">Photos</th>
                <th className="px-3 py-2 text-center">Lignes</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-left">Par</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const st = STATUS_LABELS[r.status] || STATUS_LABELS.draft;
                const hasDeletionReq = !!r.deletion_requested;
                return (
                  <tr key={r.id} className={`border-b border-zinc-100 hover:bg-purple-50/30 ${r.is_deleted ? 'opacity-60' : ''}`} data-testid={`reception-row-${r.id}`}>
                    <td className="px-3 py-2 text-zinc-700 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-3 py-2 text-zinc-900">{r.fournisseur || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.bl_number || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.validation_number ? (
                        <span className="font-semibold text-emerald-700" data-testid={`reception-validation-num-${r.id}`}>
                          {r.validation_number}
                        </span>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">{r.photos?.length || 0}</td>
                    <td className="px-3 py-2 text-center">{r.lines?.length || 0}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5 items-start">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${st.cls}`}>
                          {st.label}
                        </span>
                        {hasDeletionReq && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700" title={r.deletion_requested?.reason}>
                            🗑 Suppression demandée
                          </span>
                        )}
                        {r.is_deleted && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-700">
                            🗑 En corbeille
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-500">{r.created_by_name || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => onOpen(r.id)} data-testid={`reception-open-${r.id}`}>Ouvrir</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          );
        })()
      )}
    </div>
  );
};

// Composant filtre rapide par statut (chips, mobile-first)
const ReceptionStatusFilter = ({ items, statusFilter, setStatusFilter }) => {
  const counts = items.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  const total = items.length;
  const submitted = counts.submitted || 0;
  const validated = counts.validated || 0;
  const rejected = counts.rejected || 0;
  const draft = (counts.draft || 0) + (counts.analyzed || 0);

  const Chip = ({ value, label, count, color, testid }) => {
    const active = statusFilter === value;
    return (
      <button
        type="button"
        onClick={() => setStatusFilter(value)}
        data-testid={testid}
        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-full text-sm font-medium border-2 transition active:scale-95 min-h-[40px] ${
          active
            ? `${color.activeBg} ${color.activeText} ${color.activeBorder} shadow-sm`
            : `bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300`
        }`}
      >
        <span>{label}</span>
        <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${active ? 'bg-white/20' : 'bg-zinc-100 text-zinc-600'}`}>
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="reception-status-filter">
      <Chip
        value="all" label="Toutes" count={total}
        color={{ activeBg: 'bg-[#5B3B8C]', activeText: 'text-white', activeBorder: 'border-[#5B3B8C]' }}
        testid="filter-all"
      />
      <Chip
        value="submitted" label="🟠 À valider" count={submitted}
        color={{ activeBg: 'bg-amber-500', activeText: 'text-white', activeBorder: 'border-amber-500' }}
        testid="filter-submitted"
      />
      <Chip
        value="validated" label="✅ Validées" count={validated}
        color={{ activeBg: 'bg-emerald-600', activeText: 'text-white', activeBorder: 'border-emerald-600' }}
        testid="filter-validated"
      />
      <Chip
        value="rejected" label="❌ Rejetées" count={rejected}
        color={{ activeBg: 'bg-red-500', activeText: 'text-white', activeBorder: 'border-red-500' }}
        testid="filter-rejected"
      />
      <Chip
        value="draft" label="📝 Brouillons" count={draft}
        color={{ activeBg: 'bg-zinc-600', activeText: 'text-white', activeBorder: 'border-zinc-600' }}
        testid="filter-draft"
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Création d'une réception
// ─────────────────────────────────────────────────────────────
const NewReceptionForm = ({ onCancel, onCreated }) => {
  const [fournisseur, setFournisseur] = useState('');
  const [blNumber, setBlNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    axios.get(`${API}/receptions/suppliers`, { headers: auth() })
      .then((r) => setSuppliers(r.data?.suppliers || []))
      .catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/receptions`,
        { fournisseur: (fournisseur || '').trim() || null, bl_number: (blNumber || '').trim() || null },
        { headers: auth() }
      );
      toast.success('Réception créée');
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl mx-auto bg-white border border-zinc-200 rounded-lg p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Truck className="w-5 h-5 text-[#5B3B8C]" />
        <h3 className="text-lg font-semibold">Nouvelle réception</h3>
      </div>

      <div>
        <label className="block">
          <span className="text-xs text-zinc-500">Fournisseur</span>
        </label>
        <div
          className="flex flex-nowrap gap-1.5 mt-1.5 overflow-x-auto scrollbar-thin -mx-1 px-1 pb-1.5"
          style={{ scrollbarWidth: 'thin' }}
          data-testid="reception-supplier-chips"
        >
          {suppliers.map((s) => {
            const selected = (fournisseur || '').trim().toLowerCase() === s.name.toLowerCase();
            return (
              <button
                key={s.name}
                type="button"
                onClick={() => { setFournisseur(s.name); setShowCustom(false); }}
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap transition ${
                  selected
                    ? 'bg-[#5B3B8C] border-[#5B3B8C] text-white'
                    : s.is_default
                      ? 'border-purple-200 text-[#5B3B8C] bg-purple-50 hover:bg-purple-100'
                      : 'border-zinc-300 text-zinc-700 bg-white hover:bg-zinc-50'
                }`}
                data-testid={`supplier-chip-${s.name.replace(/\s+/g, '-')}`}
              >
                {s.name}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => { setShowCustom(true); setFournisseur(''); }}
            className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border-2 border-dashed whitespace-nowrap transition ${
              showCustom
                ? 'border-[#5B3B8C] text-[#5B3B8C] bg-purple-50'
                : 'border-zinc-300 text-zinc-500 hover:border-[#5B3B8C] hover:text-[#5B3B8C]'
            }`}
            data-testid="supplier-chip-other"
          >
            <Plus className="w-3 h-3 inline mr-0.5" />Autre
          </button>
        </div>
        {(showCustom || (fournisseur && !suppliers.find((s) => s.name.toLowerCase() === fournisseur.toLowerCase()))) && (
          <Input
            value={fournisseur}
            onChange={(e) => setFournisseur(e.target.value)}
            placeholder="Nouveau fournisseur"
            className="mt-2 h-9 text-sm"
            autoFocus={showCustom}
            data-testid="reception-fournisseur"
          />
        )}
        {fournisseur && !showCustom && (
          <p className="text-xs text-zinc-500 mt-1.5">
            Sélectionné : <strong>{fournisseur}</strong>{' '}
            <button type="button" onClick={() => setFournisseur('')} className="text-red-500 hover:underline ml-1">effacer</button>
          </p>
        )}
      </div>

      <label className="block">
        <span className="text-xs text-zinc-500">N° Bordereau de livraison</span>
        <Input value={blNumber} onChange={(e) => setBlNumber(e.target.value)}
          placeholder="ex: BL-2026-001" data-testid="reception-bl" />
      </label>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel} className="flex-1">Annuler</Button>
        <Button onClick={submit} disabled={busy} className="flex-1 bg-[#5B3B8C] hover:bg-[#4A2F73] text-white"
          data-testid="reception-create-submit">
          {busy ? 'Création…' : 'Démarrer'}
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Détail d'une réception : photos + analyse IA + lignes
// ─────────────────────────────────────────────────────────────
const ReceptionDetailView = ({ receptionId, onBack }) => {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingBL, setAnalyzingBL] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validatorInfo, setValidatorInfo] = useState(null);
  const [delActionBusy, setDelActionBusy] = useState(false);
  const isPrivileged = usePrivileged();

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/receptions/${receptionId}`, { headers: auth() });
      setRec(r.data);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally { setLoading(false); }
  }, [receptionId]);
  useEffect(() => { reload(); }, [reload]);

  // Charge les infos de validateur (qui suis-je, suis-je validateur ?)
  useEffect(() => {
    axios.get(`${API}/receptions/validators`, { headers: auth() })
      .then((r) => setValidatorInfo(r.data))
      .catch(() => {});
  }, []);

  const submitReception = async () => {
    const nbLines = rec?.lines?.length || 0;
    const nbUnits = (rec?.lines || []).reduce((s, ln) => s + (parseInt(ln.qty_validated) || 0), 0);
    const ok = window.confirm(
      `Confirmer la soumission de cette réception ?\n\n` +
      `• ${nbLines} produit(s)\n• ${nbUnits} unité(s) totales\n\n` +
      `Une fois soumise, vous ne pourrez plus la modifier. Le superviseur sera notifié par email.`
    );
    if (!ok) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/submit`, {}, { headers: auth() });
      toast.success('Réception soumise au superviseur ✓');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la soumission');
    } finally { setSubmitting(false); }
  };

  const validateReception = async () => {
    if (!window.confirm('Valider définitivement cette réception ?')) return;
    setValidating(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/validate`, {}, { headers: auth() });
      toast.success('Réception validée ✓');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur validation');
    } finally { setValidating(false); }
  };

  const rejectReception = async () => {
    const reason = window.prompt('Motif du rejet (sera communiqué à l\'agent) :');
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error('Motif trop court (min 3 caractères)');
      return;
    }
    setValidating(true);
    try {
      await axios.post(
        `${API}/receptions/${receptionId}/reject`,
        { reason: reason.trim() },
        { headers: auth() }
      );
      toast.success('Réception rejetée — agent notifié');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur rejet');
    } finally { setValidating(false); }
  };

  // ── Workflow suppression ────────────────────────────────────────
  const requestDeletion = async () => {
    const reason = window.prompt('Pourquoi voulez-vous supprimer cette réception ? (≥10 caractères, sera vu par l\'admin)');
    if (!reason) return;
    if (reason.trim().length < 10) {
      toast.error('Motif trop court (min 10 caractères)');
      return;
    }
    setDelActionBusy(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/request-deletion`, { reason: reason.trim() }, { headers: auth() });
      toast.success('Demande envoyée à l\'admin ✓');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur demande');
    } finally { setDelActionBusy(false); }
  };

  const cancelDeletionRequest = async () => {
    if (!window.confirm('Annuler la demande de suppression ?')) return;
    setDelActionBusy(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/cancel-deletion-request`, {}, { headers: auth() });
      toast.success('Demande annulée');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setDelActionBusy(false); }
  };

  const softDelete = async () => {
    const reason = window.prompt('Commentaire de suppression (≥10 caractères, sera journalisé) :');
    if (!reason) return;
    if (reason.trim().length < 10) {
      toast.error('Commentaire trop court (min 10 caractères)');
      return;
    }
    setDelActionBusy(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/soft-delete`, { reason: reason.trim() }, { headers: auth() });
      toast.success('Réception envoyée en corbeille (réhabilitable)');
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    } finally { setDelActionBusy(false); }
  };

  const restoreReception = async () => {
    if (!window.confirm('Restaurer cette réception depuis la corbeille ?')) return;
    setDelActionBusy(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/restore`, {}, { headers: auth() });
      toast.success('Réception restaurée ✓');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setDelActionBusy(false); }
  };

  const hardDelete = async () => {
    if (!window.confirm('SUPPRESSION DÉFINITIVE — irréversible. Continuer ?')) return;
    setDelActionBusy(true);
    try {
      await axios.delete(`${API}/receptions/${receptionId}`, { headers: auth() });
      toast.success('Réception supprimée définitivement');
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setDelActionBusy(false); }
  };

  const unvalidateReception = async () => {
    const reason = window.prompt('Motif de la dévalidation (≥10 caractères) :');
    if (!reason) return;
    if (reason.trim().length < 10) {
      toast.error('Motif trop court (min 10 caractères)');
      return;
    }
    setDelActionBusy(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/unvalidate`, { reason: reason.trim() }, { headers: auth() });
      toast.success('Réception dévalidée — vous pouvez maintenant la supprimer');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setDelActionBusy(false); }
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      try {
        // Pré-traitement : resize 2400px + boost contraste pour OCR optimal
        const { blob } = await preprocessBL(f);
        const fd = new FormData();
        const optimized = new File([blob], f.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
        fd.append('file', optimized);
        await axios.post(`${API}/receptions/${receptionId}/photos`, fd, {
          headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
        });
        ok++;
      } catch (e) {
        fail++;
      }
    }
    setUploading(false);
    await reload();
    if (ok) toast.success(`${ok} photo(s) ajoutée(s) (optimisées)`);
    if (fail) toast.error(`${fail} échec(s)`);
  };

  const handleCameraCapture = async (blob) => {
    // Pré-traitement avant upload pour améliorer la reconnaissance Claude Vision
    const { blob: optimized } = await preprocessBL(blob);
    const fd = new FormData();
    const file = new File([optimized], `bl-${Date.now()}.jpg`, { type: 'image/jpeg' });
    fd.append('file', file);
    await axios.post(`${API}/receptions/${receptionId}/photos`, fd, {
      headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
    });
    await reload();
  };

  const removePhoto = async (photoId) => {
    if (!confirm('Supprimer cette photo ?')) return;
    try {
      await axios.delete(`${API}/receptions/${receptionId}/photos/${photoId}`, { headers: auth() });
      await reload();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const analyze = async () => {
    if (!rec?.photos?.length) {
      toast.error('Ajoutez au moins une photo');
      return;
    }
    setAnalyzing(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/analyze`, {}, { headers: auth() });
      await reload();
      toast.success('Analyse IA terminée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur IA');
    } finally { setAnalyzing(false); }
  };

  const analyzeBL = async () => {
    if (!rec?.photos?.length) {
      toast.error('Ajoutez au moins une photo du BL');
      return;
    }
    if ((rec?.lines?.length || 0) > 0) {
      const ok = window.confirm(
        `Cette réception contient déjà ${rec.lines.length} ligne(s).\n\n` +
        `Le scan du BL va REMPLACER toutes les lignes existantes.\n\nContinuer ?`
      );
      if (!ok) return;
    }
    setAnalyzingBL(true);
    try {
      await axios.post(`${API}/receptions/${receptionId}/analyze-bl`, {}, { headers: auth() });
      await reload();
      toast.success('Bon de Livraison scanné ✓');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur scan BL');
    } finally { setAnalyzingBL(false); }
  };

  const updateLine = async (lineId, patch) => {
    try {
      await axios.put(`${API}/receptions/${receptionId}/lines/${lineId}`, patch, { headers: auth() });
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteLine = async (lineId) => {
    if (!confirm('Supprimer cette ligne ?')) return;
    try {
      await axios.delete(`${API}/receptions/${receptionId}/lines/${lineId}`, { headers: auth() });
      await reload();
    } catch (e) { toast.error('Erreur'); }
  };

  const [addLineOpen, setAddLineOpen] = useState(false);
  const [newLineText, setNewLineText] = useState('');
  const [newLineMatched, setNewLineMatched] = useState(null);
  const [newLineStatus, setNewLineStatus] = useState('empty');
  const [newLineQty, setNewLineQty] = useState(1);

  const submitNewLine = async () => {
    const t = (newLineText || '').trim();
    if (!t) { toast.error('Saisissez la désignation'); return; }
    if (/^\d+$/.test(t) && !newLineMatched) {
      toast.error('Code produit non reconnu');
      return;
    }
    const qty = parseInt(newLineQty, 10);
    if (!Number.isFinite(qty) || qty < 1) { toast.error('Quantité invalide'); return; }
    try {
      await axios.post(
        `${API}/receptions/${receptionId}/lines`,
        {
          designation: newLineMatched?.name || t,
          detected_qty: qty,
          matched_product_id: newLineMatched?.id || null,
          is_new_product: !newLineMatched && newLineStatus !== 'unrecognized',
        },
        { headers: auth() },
      );
      toast.success('Ligne ajoutée');
      setAddLineOpen(false);
      setNewLineText('');
      setNewLineMatched(null);
      setNewLineStatus('empty');
      setNewLineQty(1);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur ajout ligne');
    }
  };

  const addManualLine = () => {
    setAddLineOpen(true);
    setNewLineText('');
    setNewLineMatched(null);
    setNewLineStatus('empty');
    setNewLineQty(1);
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!rec) return null;

  // Calculs workflow
  const isEditable = (rec.status === 'draft' || rec.status === 'analyzed') && !rec.is_deleted;
  const isSubmitted = rec.status === 'submitted';
  const isValidated = rec.status === 'validated';
  const canSubmit = (rec.status === 'draft' || rec.status === 'analyzed') && (rec.lines?.length || 0) > 0;
  const isValidator = !!validatorInfo?.current_user_is_validator;
  const currentUserId = validatorInfo?.current_user_id;
  const canValidate = isSubmitted && isValidator && rec.submitted_by !== currentUserId;
  const cannotValidateSelf = isSubmitted && isValidator && rec.submitted_by === currentUserId;

  const STATUS_BADGE = {
    draft: { label: 'BROUILLON', cls: 'bg-zinc-100 text-zinc-700' },
    analyzed: { label: 'ANALYSÉE IA', cls: 'bg-blue-100 text-blue-700' },
    submitted: { label: 'EN ATTENTE DE VALIDATION', cls: 'bg-amber-100 text-amber-800' },
    validated: { label: 'VALIDÉE ✓', cls: 'bg-emerald-100 text-emerald-700' },
    rejected: { label: 'REJETÉE', cls: 'bg-red-100 text-red-700' },
  }[rec.status] || { label: rec.status, cls: 'bg-zinc-100 text-zinc-700' };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <h2 className="text-lg font-bold">
              {rec.fournisseur || 'Réception'} {rec.bl_number ? `· BL ${rec.bl_number}` : ''}
            </h2>
            <p className="text-xs text-zinc-500">
              {new Date(rec.created_at).toLocaleString('fr-FR')} · par {rec.created_by_name} · {rec.photos?.length || 0} photo(s) · {rec.lines?.length || 0} ligne(s)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded ${STATUS_BADGE.cls}`} data-testid="reception-status-badge">
            {STATUS_BADGE.label}
          </span>
          {/* Action suppression — agent : demande, admin/manager : direct */}
          {!rec.is_deleted && !rec.deletion_requested && (
            isPrivileged ? (
              <Button
                size="sm"
                variant="outline"
                onClick={rec.status === 'validated' ? unvalidateReception : softDelete}
                disabled={delActionBusy}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="reception-soft-delete-btn"
                title={rec.status === 'validated' ? 'Dévalider d\'abord (étape 1/2)' : 'Mettre à la corbeille'}
              >
                {rec.status === 'validated' ? (
                  <><Unlock className="w-4 h-4 mr-1" /> Dévalider</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-1" /> Supprimer</>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={requestDeletion}
                disabled={delActionBusy}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="reception-request-deletion-btn"
                title="Demander à l'admin de supprimer cette réception"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Demander suppression
              </Button>
            )
          )}
        </div>
      </div>

      {/* Bandeau corbeille */}
      {rec.is_deleted && (
        <div className="rounded-lg border-2 border-zinc-300 bg-zinc-100 p-4 flex items-start gap-3" data-testid="reception-trash-banner">
          <Trash className="w-5 h-5 text-zinc-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-zinc-800">🗑 Réception en corbeille</p>
            <p className="text-zinc-600 mt-1">
              Supprimée par <strong>{rec.deleted_by_name || '—'}</strong>
              {rec.deleted_at && ` le ${new Date(rec.deleted_at).toLocaleString('fr-FR')}`}.
            </p>
            {rec.deletion_comment && (
              <p className="text-zinc-700 mt-1"><span className="font-medium">Motif :</span> {rec.deletion_comment}</p>
            )}
          </div>
          {isPrivileged && (
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" onClick={restoreReception} disabled={delActionBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reception-restore-btn">
                <Undo2 className="w-4 h-4 mr-1" /> Restaurer
              </Button>
              <Button size="sm" onClick={hardDelete} disabled={delActionBusy} variant="outline" className="border-red-400 text-red-700 hover:bg-red-50" data-testid="reception-hard-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Vider définitivement
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Bandeau demande de suppression (admin voit + peut approuver/refuser) */}
      {!rec.is_deleted && rec.deletion_requested && (
        <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="reception-deletion-request-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-red-800">🗑 Demande de suppression en attente</p>
            <p className="text-red-700 mt-1">
              Demandée par <strong>{rec.deletion_requested.by_name}</strong>
              {rec.deletion_requested.at && ` le ${new Date(rec.deletion_requested.at).toLocaleString('fr-FR')}`}.
            </p>
            <p className="text-red-700 mt-1"><span className="font-medium">Motif :</span> {rec.deletion_requested.reason}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {isPrivileged && (
              <Button size="sm" onClick={rec.status === 'validated' ? unvalidateReception : softDelete} disabled={delActionBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reception-approve-deletion-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {rec.status === 'validated' ? 'Dévalider d\'abord' : 'Approuver suppression'}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={cancelDeletionRequest} disabled={delActionBusy} data-testid="reception-cancel-deletion-btn">
              <X className="w-4 h-4 mr-1" /> Annuler la demande
            </Button>
          </div>
        </div>
      )}

      {/* === Bandeau workflow === */}
      {rec.status === 'analyzed' && rec.rejection_reason && (
        <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="reception-rejection-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-red-800">Rejetée par {rec.rejected_by_name || 'le superviseur'}</p>
            <p className="text-red-700 mt-1"><span className="font-medium">Motif :</span> {rec.rejection_reason}</p>
            <p className="text-red-600 text-xs mt-1">Corrigez puis soumettez à nouveau.</p>
          </div>
        </div>
      )}
      {isSubmitted && (
        <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="reception-submitted-banner">
          <Loader2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 animate-spin" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-amber-900">En attente de validation par superviseur</p>
            <p className="text-amber-800 mt-1">
              Soumise par <strong>{rec.submitted_by_name}</strong>{rec.submitted_at && ` le ${new Date(rec.submitted_at).toLocaleString('fr-FR')}`}.
            </p>
            {cannotValidateSelf && (
              <p className="text-amber-700 text-xs mt-1 italic">
                ⚠ Vous ne pouvez pas valider votre propre soumission (séparation des pouvoirs).
              </p>
            )}
          </div>
          {canValidate && (
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                onClick={validateReception}
                disabled={validating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="reception-validate-btn"
              >
                {validating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Valider
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={rejectReception}
                disabled={validating}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="reception-reject-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Rejeter
              </Button>
            </div>
          )}
        </div>
      )}
      {isValidated && (
        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4" data-testid="reception-validated-banner">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm flex-1">
              <p className="font-semibold text-emerald-800">Réception validée ✓</p>
              <p className="text-emerald-700 mt-1">
                Validée par <strong>{rec.validated_by_name}</strong>{rec.validated_at && ` le ${new Date(rec.validated_at).toLocaleString('fr-FR')}`}.
              </p>
            </div>
          </div>
          {rec.validation_number && (
            <div className="mt-3 rounded-lg bg-white border-2 border-emerald-300 p-4">
              <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
                Numéro de validation à reporter sur le BL papier
              </p>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span
                  className="font-mono font-bold text-2xl sm:text-3xl text-emerald-900 tracking-wider select-all"
                  data-testid="reception-validation-number"
                >
                  {rec.validation_number}
                </span>
                <div className="flex gap-2 flex-wrap w-full sm:w-auto">
                  <Button
                    variant="outline"
                    className="flex-1 sm:flex-none h-12 sm:h-9 text-base sm:text-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 active:bg-emerald-100 font-semibold"
                    data-testid="reception-copy-validation-number"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(rec.validation_number);
                        toast.success('Numéro copié', { duration: 2500 });
                      } catch {
                        toast.error('Copie indisponible — recopiez manuellement');
                      }
                    }}
                  >
                    📋 Copier
                  </Button>
                  <Button
                    className="flex-1 sm:flex-none h-12 sm:h-9 text-base sm:text-sm bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold"
                    data-testid="reception-print-label-btn"
                    onClick={async () => {
                      try {
                        const r = await axios.get(
                          `${API}/receptions/${rec.id}/validation-label`,
                          { headers: auth(), responseType: 'blob' }
                        );
                        const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                        const w = window.open(blobUrl, '_blank');
                        if (!w) {
                          const a = document.createElement('a');
                          a.href = blobUrl;
                          a.download = `etiquette_${rec.validation_number}.pdf`;
                          a.click();
                        }
                        toast.success('Étiquette générée — imprimez-la (80×50 mm)');
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Erreur génération étiquette');
                      }
                    }}
                  >
                    🏷 Imprimer l'étiquette
                  </Button>
                </div>
              </div>
              <p className="text-xs text-emerald-600 mt-2">
                ✍ Reportez ce numéro sur le BL ou imprimez l'étiquette (80×50 mm avec QR code) à coller dessus.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Section photos */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#5B3B8C]" /> Photos ({rec.photos?.length || 0})
            {rec.analyze_mode === 'bl' && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 ml-1">
                MODE BL
              </span>
            )}
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setCameraOpen(true)}
              disabled={!isEditable}
              className="bg-[#5B3B8C] hover:bg-[#4A2F73] text-white"
              data-testid="reception-camera-btn"
            >
              <Camera className="w-4 h-4 mr-1" /> Prendre une photo
            </Button>
            <label className={`cursor-pointer ${!isEditable ? 'opacity-40 pointer-events-none' : ''}`}>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                disabled={!isEditable}
                className="hidden"
                onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                data-testid="reception-photo-input"
              />
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-purple-300 text-[#5B3B8C] hover:bg-purple-50 text-sm">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {uploading ? 'Upload…' : 'Importer photos'}
              </span>
            </label>
            <Button
              onClick={analyzeBL}
              disabled={analyzingBL || analyzing || !rec.photos?.length || !isEditable}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              data-testid="reception-analyze-bl-btn"
              title="Scanner un Bon de Livraison entier (CIP imprimés + désignations + quantités)"
            >
              {analyzingBL ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1" />}
              {analyzingBL ? 'Lecture du BL…' : 'Scanner le BL'}
            </Button>
            <Button
              onClick={analyze}
              disabled={analyzing || analyzingBL || !rec.photos?.length || !isEditable}
              variant="outline"
              className="border-purple-300 text-[#5B3B8C] hover:bg-purple-50"
              data-testid="reception-analyze-btn"
              title="Mode photos individuelles (1 photo = 1 produit)"
            >
              {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {analyzing ? 'IA…' : 'Mode photos produits'}
            </Button>
          </div>
        </div>

        {rec.photos?.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
            {rec.photos.map((p) => (
              <div key={p.photo_id} className="relative group" data-testid={`photo-${p.photo_id}`}>
                <img
                  src={`${FILE_BASE}${p.url}`}
                  alt=""
                  className="w-full aspect-square object-cover rounded border border-zinc-200"
                />
                <button
                  onClick={() => removePhoto(p.photo_id)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition"
                  title="Supprimer"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border-2 border-dashed rounded text-zinc-500 text-sm">
            <ImageIcon className="w-8 h-8 mx-auto mb-1 text-zinc-300" />
            Aucune photo. Cliquez sur « Ajouter photos » pour commencer.
          </div>
        )}
      </div>

      {/* Section lignes (si analyse faite) */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Package className="w-4 h-4 text-[#5B3B8C]" /> Produits détectés ({rec.lines?.length || 0})
          </h3>
          <Button size="sm" variant="outline" onClick={addManualLine} disabled={!isEditable}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter manuellement
          </Button>
        </div>

        {/* Panneau d'ajout inline avec autocomplétion */}
        {addLineOpen && (
          <div className="bg-purple-50/50 border border-purple-200 rounded-lg p-3 mt-2 space-y-2" data-testid="add-line-panel">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-start">
              <div>
                <label className="text-[10px] uppercase font-semibold text-zinc-500">Produit</label>
                <ProductAutocomplete
                  value={newLineText}
                  onChange={(t, m, st) => { setNewLineText(t); setNewLineMatched(m); setNewLineStatus(st); }}
                  placeholder="Tapez 3 lettres ou un code"
                  autoFocus
                  testId="new-line-autocomplete"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold text-zinc-500">Qté</label>
                <Input
                  type="number"
                  min="1"
                  value={newLineQty}
                  onChange={(e) => setNewLineQty(e.target.value)}
                  className="w-20 text-center"
                  data-testid="new-line-qty"
                />
              </div>
              <div className="flex items-end gap-1 h-full">
                <Button
                  size="sm"
                  onClick={submitNewLine}
                  className="bg-[#5B3B8C] hover:bg-[#4A2F73] text-white"
                  data-testid="new-line-save"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Ajouter
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAddLineOpen(false)} data-testid="new-line-cancel">
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        )}

        {!rec.lines?.length ? (
          <div className="text-center py-6 text-zinc-500 text-sm border-2 border-dashed rounded">
            {rec.photos?.length
              ? "Choisissez : « Scanner le BL » (recommandé si bordereau détaillé) ou « Mode photos produits » (1 photo = 1 produit)."
              : "Ajoutez la photo du Bon de Livraison ou des produits, puis lancez l'analyse IA."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-2 py-2 text-left">Produit</th>
                  <th className="px-2 py-2 text-left">Code (CIP/EAN)</th>
                  <th className="px-2 py-2 text-center">Qté</th>
                  <th className="px-2 py-2 text-center">Photos</th>
                  <th className="px-2 py-2 text-left">Match Mediciel</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rec.lines.map((l) => (
                  <ReceptionLineRow
                    key={l.id}
                    line={l}
                    photos={rec.photos}
                    onUpdate={(patch) => updateLine(l.id, patch)}
                    onDelete={() => deleteLine(l.id)}
                    isEditable={isEditable}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* === Bandeau de soumission (sticky bottom sur analysed) === */}
      {canSubmit && (
        <div className="sticky bottom-0 z-10 -mx-2 sm:mx-0 bg-white border-2 border-emerald-200 rounded-lg shadow-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="reception-submit-bar">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-zinc-900">Réception prête à être soumise</p>
              <p className="text-sm text-zinc-500">
                {rec.lines?.length || 0} produit(s) · {(rec.lines || []).reduce((s, ln) => s + (parseInt(ln.qty_validated) || 0), 0)} unité(s) totales
              </p>
            </div>
          </div>
          <Button
            onClick={submitReception}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-5 text-base"
            data-testid="reception-submit-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
            {submitting ? 'Envoi…' : 'Terminer & soumettre au superviseur'}
          </Button>
        </div>
      )}

      {/* Camera dialog */}
      <BLCameraDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
};

const ReceptionLineRow = ({ line, photos, onUpdate, onDelete, isEditable = true }) => {
  const [editing, setEditing] = useState(false);
  const [draftDesignation, setDraftDesignation] = useState(line.designation || '');
  const [draftQty, setDraftQty] = useState(line.qty_validated ?? line.detected_qty ?? 0);
  const [draftMatched, setDraftMatched] = useState(null);
  const [draftStatus, setDraftStatus] = useState('empty');
  const [lotsOpen, setLotsOpen] = useState(false);
  const photoUrls = (line.photo_ids || [])
    .map((pid) => photos.find((p) => p.photo_id === pid)?.url)
    .filter(Boolean);
  // Badge ligne (vue lecture) — gère le statut du mode BL en priorité
  const matchBadge = line.match_status === 'Reconnu' || (!line.is_new_product && line.matched_product_id && line.match_score >= 0.9)
    ? { label: 'Reconnu ✓', cls: 'bg-emerald-100 text-emerald-700' }
    : line.match_status === 'Suggestion' || (line.matched_product_id && line.match_score < 0.9)
      ? { label: 'Suggestion', cls: 'bg-blue-100 text-blue-700' }
      : line.is_new_product
        ? { label: 'Nouveau produit', cls: 'bg-violet-100 text-violet-700' }
        : line.unknown
          ? { label: 'Non reconnu', cls: 'bg-red-100 text-red-700' }
          : { label: 'À vérifier', cls: 'bg-amber-100 text-amber-700' };

  const save = () => {
    // Bloque la sauvegarde si chiffres uniquement et pas de match
    if (/^\d+$/.test((draftDesignation || '').trim()) && !draftMatched) {
      toast.error('Code produit non reconnu — saisissez le nom complet ou choisissez une suggestion.');
      return;
    }
    const patch = {};
    if (draftDesignation !== line.designation) patch.designation = draftDesignation;
    if (parseInt(draftQty, 10) !== (line.qty_validated ?? line.detected_qty)) {
      patch.detected_qty = parseInt(draftQty, 10) || 0;
    }
    if (draftMatched?.id && draftMatched.id !== line.matched_product_id) {
      patch.matched_product_id = draftMatched.id;
      patch.is_new_product = false;
    }
    if (draftStatus === 'newProduct' && !draftMatched) {
      patch.matched_product_id = null;
      patch.is_new_product = true;
    }
    if (Object.keys(patch).length) onUpdate(patch);
    setEditing(false);
  };

  return (
    <>
    <tr className="border-b border-zinc-100" data-testid={`reception-line-${line.id}`}>
      <td className="px-2 py-2">
        {editing ? (
          <ProductAutocomplete
            value={draftDesignation}
            onChange={(text, matched, status) => {
              setDraftDesignation(text);
              setDraftMatched(matched);
              setDraftStatus(status);
            }}
            autoFocus
            testId={`line-${line.id}-autocomplete`}
          />
        ) : (
          <div>
            <div className="font-medium">{line.designation || <span className="italic text-zinc-400">Non identifié</span>}</div>
            <span className={`inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${matchBadge.cls}`}>
              {matchBadge.label}
            </span>
            {line.matched_product_designation && line.matched_product_designation !== line.designation && (
              <div className="text-xs text-zinc-500 mt-0.5">
                Catalogue : {line.matched_product_designation}
              </div>
            )}
          </div>
        )}
      </td>
      <td className="px-2 py-2 font-mono text-xs">{line.cip || line.ean || '—'}</td>
      <td className="px-2 py-2 text-center">
        {editing ? (
          <Input type="number" min="0" value={draftQty} onChange={(e) => setDraftQty(e.target.value)}
            className="w-16 text-sm text-center" />
        ) : (
          <span className="text-base font-bold">{line.qty_validated ?? line.detected_qty}</span>
        )}
      </td>
      <td className="px-2 py-2 text-center">
        <div className="flex gap-0.5 justify-center">
          {photoUrls.slice(0, 3).map((u, i) => (
            <img key={i} src={`${FILE_BASE}${u}`} alt="" className="w-8 h-8 object-cover rounded border" />
          ))}
          {photoUrls.length > 3 && (
            <span className="text-xs text-zinc-500 self-end ml-1">+{photoUrls.length - 3}</span>
          )}
        </div>
      </td>
      <td className="px-2 py-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${matchBadge.cls}`}>
          {matchBadge.label}
        </span>
        {line.match_score > 0 && (
          <span className="ml-1 text-[10px] text-zinc-500">
            ({Math.round(line.match_score * 100)}%)
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-right whitespace-nowrap">
        {!isEditable ? (
          <span className="text-xs text-zinc-400 italic">Verrouillée</span>
        ) : editing ? (
          <div className="inline-flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="min-h-[44px] sm:min-h-9">Annuler</Button>
            <Button size="sm" onClick={save} className="min-h-[44px] sm:min-h-9 bg-[#5B3B8C] text-white">
              <CheckCircle2 className="w-4 h-4 mr-1" /> OK
            </Button>
          </div>
        ) : (
          <div className="inline-flex gap-1 flex-wrap justify-end">
            {/* Bouton "Accepter suggestion" si la ligne est une Suggestion */}
            {(line.match_status === 'Suggestion' ||
              (!line.is_new_product && line.matched_product_id && (line.match_score || 0) > 0 && (line.match_score || 0) < 0.9)) && (
              <Button
                size="sm"
                onClick={() => onUpdate({
                  designation: line.matched_product_designation || line.designation,
                  is_new_product: false,
                  match_status: 'Reconnu',
                })}
                className="min-h-[44px] sm:min-h-9 bg-blue-600 hover:bg-blue-700 text-white"
                title="Confirmer la suggestion du catalogue"
                data-testid={`accept-suggestion-${line.id}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Accepter
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditing(true); setDraftDesignation(line.designation || ''); setDraftQty(line.qty_validated ?? line.detected_qty ?? 0); }}
              className="min-h-[44px] sm:min-h-9"
            >
              Modifier
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setLotsOpen(true)}
              className="min-h-[44px] sm:min-h-9 border-teal-300 text-teal-700 hover:bg-teal-50 active:bg-teal-100"
              data-testid={`reception-line-lots-btn-${line.id}`}
              title={(line.lots?.length || 0) > 0 ? `${line.lots.length} lot(s) saisi(s)` : 'Saisir lot(s) & péremption'}
            >
              <Layers className="w-4 h-4 mr-1" />
              {(line.lots?.length || 0) > 0 ? `Lots (${line.lots.length})` : 'Lots'}
            </Button>
            <button
              onClick={onDelete}
              className="min-h-[44px] min-w-[44px] sm:min-h-9 sm:min-w-9 flex items-center justify-center text-red-500 hover:bg-red-50 active:bg-red-100 rounded-lg"
              aria-label="Supprimer cette ligne"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </td>
    </tr>
    {lotsOpen && (
      <ReceptionLotsDialog
        line={line}
        onClose={() => setLotsOpen(false)}
        onSave={async (lots) => {
          await onUpdate({ lots });
          toast.success(`${lots.length} lot(s) enregistré(s)`);
        }}
      />
    )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Tab principal — switch entre liste / formulaire / détail
// ─────────────────────────────────────────────────────────────
const ReceptionTab = () => {
  const [view, setView] = useState('list'); // list | new | detail
  const [activeId, setActiveId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  if (view === 'new') {
    return <NewReceptionForm
      onCancel={() => setView('list')}
      onCreated={(id) => { setActiveId(id); setView('detail'); }}
    />;
  }
  if (view === 'detail' && activeId) {
    return <ReceptionDetailView
      receptionId={activeId}
      onBack={() => { setView('list'); setRefreshKey(k => k + 1); }}
    />;
  }
  return <ReceptionListView
    key={refreshKey}
    onCreate={() => setView('new')}
    onOpen={(id) => { setActiveId(id); setView('detail'); }}
  />;
};

export default ReceptionTab;
